import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, ArrowRight, Trash2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { EmptyState } from '../components/common/EmptyState';
import { StatusBadge } from '../components/common/StatusBadge';
import UpgradePrompt from '../components/common/UpgradePrompt';
import { Skeleton } from '../components/ui/skeleton';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';

/**
 * DealsPage Component
 * Per PocketFiler UI Style Guide - uses SVG icons from asset pack, no emojis
 */

import { MultiSelect } from '../components/ui/multi-select';
import { canWrite, canManageTeam, canManageBilling, isViewer } from '../lib/roleUtils';

// SVG Icon component
const SvgIcon = ({ src, className = 'h-5 w-5' }) => (
  <img src={`/assets/${src}`} alt="" className={className} aria-hidden="true" />
);

const dealTypes = [
  { value: 'import_export', label: 'Import/Export' },
  { value: 'insurance_policy', label: 'Insurance Policy' },
  { value: 'vehicle_sale', label: 'Vehicle Sale' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'service_contract', label: 'Service Contract' },
  { value: 'other', label: 'Other' }
];

export default function DealsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: 'other',
    summary: '',
    client_ids: [],
    virtual_office_id: ''
  });

  const [errors, setErrors] = useState({});

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const [dealToDelete, setDealToDelete] = useState(null);

  const { data: deals, isLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: () => api.get('/deals')
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/clients')
  });

  const { data: offices } = useQuery({
    queryKey: ['virtual-offices'],
    queryFn: () => api.get('/virtual-offices')
  });

  const { data: orgPlanInfo } = useQuery({
    queryKey: ['org-plan', user?.organization_id],
    queryFn: () => api.get(`/organizations/${user.organization_id}/plan`),
    enabled: !!user?.organization_id
  });

  const hasLimit = true; // All plans check limits (backend enforces)
  const dealLimit = orgPlanInfo?.limits?.max_active_deals ?? -1;
  const myDeals = deals?.filter(d => d.owner_user_id === user?._id) || [];
  const activeDealsCount = myDeals.filter(d => !['closed', 'cancelled'].includes(d.status)).length;
  const atDealLimit = hasLimit && dealLimit !== -1 && activeDealsCount >= dealLimit;

  // Contract status counts for filter tabs
  const statusCounts = {
    draft: deals?.filter(d => d.status === 'draft' || d.status === 'new').length || 0,
    pending: deals?.filter(d => d.status === 'pending_signature').length || 0,
    signed: deals?.filter(d => d.status === 'completed' || d.status === 'fully_signed').length || 0,
    all: deals?.length || 0,
  };

  // Filter deals based on contract status filter
  const getFilteredDeals = (dealsList) => {
    if (!dealsList) return dealsList;
    if (statusFilter === 'draft') return dealsList.filter(d => d.status === 'draft' || d.status === 'new');
    if (statusFilter === 'pending') return dealsList.filter(d => d.status === 'pending_signature');
    if (statusFilter === 'signed') return dealsList.filter(d => d.status === 'completed' || d.status === 'fully_signed');
    return dealsList;
  };

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/deals', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['deals']);
      toast.success(t('success.created'));
      setDialogOpen(false);
      setFormData({ title: '', type: 'other', summary: '', client_ids: [], virtual_office_id: '' });
      setErrors({});
    },
    onError: (error) => {
      toast.error(error.message || t('error.generic'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/deals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['deals']);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDealToDelete(null);
    }
  });

  const handleDeleteClick = (dealId) => {
    setDealToDelete(dealId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (dealToDelete) {
      deleteMutation.mutate(dealToDelete);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // 1. Deal title required and max 100 chars
    if (!formData.title.trim()) {
      newErrors.title = t('deals.validation.titleRequired');
    } else if (formData.title.length > 100) {
      newErrors.title = t('deals.validation.titleLength');
    }

    // 2. Client required
    if (!formData.client_ids || formData.client_ids.length === 0) {
      newErrors.client_ids = t('deals.validation.clientRequired');
    }

    // 3. Notes max 200 chars
    if (formData.summary && formData.summary.length > 200) {
      newErrors.summary = t('deals.validation.notesLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('error.validation'));
      return;
    }

    const cleanedData = {
      ...formData,
      virtual_office_id: formData.virtual_office_id === 'none' ? null : formData.virtual_office_id
    };
    createMutation.mutate(cleanedData);
  };


  return (
    <div className="space-y-6 animate-fade-in" data-testid="deals-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">{t('deals.title')}</h1>
          <p className="text-muted-foreground">{t('deals.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasLimit && dealLimit !== -1 && (
            <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
              {activeDealsCount} / {dealLimit} active deals (My Deals)
            </span>
          )}
          <Button
              onClick={() => navigate('/quick-contract')}
              data-testid="new-contract-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Contract
            </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="create-deal-button"
                disabled={atDealLimit}
                title={atDealLimit ? `Active deal limit reached (${dealLimit} on current plan)` : undefined}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('deals.create')}
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="p-6 pb-2 flex-shrink-0">
                <DialogTitle className="flex items-center gap-2">
                  <SvgIcon src="briefcase.svg" className="h-5 w-5" />
                  {t('deals.dialog.title')}
                </DialogTitle>
                <DialogDescription>{t('deals.subtitle')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-2">
                  <div className="space-y-4 py-2">
                    <div className="space-y-2">
                      <Label htmlFor="title">
                        {t('deals.dialog.dealTitle')} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="title"
                        placeholder={t('deals.dialog.dealTitlePlaceholder')}
                        value={formData.title}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, title: e.target.value }));
                          if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                        }}
                        className={errors.title ? "border-red-500" : ""}
                        data-testid="deal-title-input"
                      />
                      {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
                      <p className="text-xs text-muted-foreground text-right">{formData.title.length}/100</p>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('deals.dialog.type')}</Label>
                      <Select
                        value={formData.type}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                      >
                        <SelectTrigger data-testid="deal-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dealTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>
                          {t('deals.dialog.client')} <span className="text-red-500">*</span>
                        </Label>
                        <MultiSelect
                          options={clients?.map(c => ({ value: c._id, label: c.name || 'Unnamed Client' })) || []}
                          selected={formData.client_ids}
                          onChange={(values) => {
                            setFormData(prev => ({ ...prev, client_ids: values }));
                            if (errors.client_ids) setErrors(prev => ({ ...prev, client_ids: null }));
                          }}
                          placeholder={t('common.search')}
                          emptyText={t('common.noResults')}
                          className={errors.client_ids ? "border-red-500" : ""}
                        />
                        {errors.client_ids && <p className="text-xs text-red-500">{errors.client_ids}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('deals.dialog.office')}</Label>
                        <Select
                          value={formData.virtual_office_id}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, virtual_office_id: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('common.search')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('common.optional')}</SelectItem>
                            {offices?.map(office => (
                              <SelectItem key={office._id} value={office._id}>{office.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="summary">{t('deals.dialog.notes')}</Label>
                      <Textarea
                        id="summary"
                        placeholder={t('deals.dialog.notesPlaceholder')}
                        value={formData.summary}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, summary: e.target.value }));
                          if (errors.summary) setErrors(prev => ({ ...prev, summary: null }));
                        }}
                        rows={3}
                        className={errors.summary ? "border-red-500" : ""}
                      />
                      {errors.summary && <p className="text-xs text-red-500">{errors.summary}</p>}
                      <p className="text-xs text-muted-foreground text-right">{formData.summary.length}/200</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
                  <DialogFooter className="pt-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending}>
                      {createMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t('deals.dialog.creating')}
                        </>
                      ) : (
                        t('deals.dialog.create')
                      )}
                    </Button>
                  </DialogFooter>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Contract Status Summary */}
      <div className="grid grid-cols-4 gap-3">
        <button
          onClick={() => setStatusFilter('all')}
          className={`p-3 rounded-lg border text-center transition-colors ${statusFilter === 'all' ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
        >
          <div className="text-2xl font-bold">{statusCounts.all}</div>
          <div className="text-xs text-muted-foreground">{t("deals.filter.all") || "All"}</div>
        </button>
        <button
          onClick={() => setStatusFilter('draft')}
          className={`p-3 rounded-lg border text-center transition-colors ${statusFilter === 'draft' ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
        >
          <div className="text-2xl font-bold">{statusCounts.draft}</div>
          <div className="text-xs text-muted-foreground">{t("deals.filter.drafts") || "Drafts"}</div>
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`p-3 rounded-lg border text-center transition-colors ${statusFilter === 'pending' ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
        >
          <div className="text-2xl font-bold">{statusCounts.pending}</div>
          <div className="text-xs text-muted-foreground">{t("deals.filter.pendingSignature") || "Pending Signature"}</div>
        </button>
        <button
          onClick={() => setStatusFilter('signed')}
          className={`p-3 rounded-lg border text-center transition-colors ${statusFilter === 'signed' ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
        >
          <div className="text-2xl font-bold">{statusCounts.signed}</div>
          <div className="text-xs text-muted-foreground">{t("deals.filter.signed") || "Signed"}</div>
        </button>
      </div>

      {/* Plan upgrade prompt when at limit */}
      {atDealLimit && (
        <UpgradePrompt
          feature="Creating more deals"
          message={`You've reached the ${dealLimit}-active-deal limit on your current plan. Upgrade for more.`}
        />
      )}

      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">{t('deals.tabs.all') || 'All Deals'}</TabsTrigger>
          <TabsTrigger value="my">{t('deals.tabs.my') || 'My Deals'}</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <DealsBoard
            deals={getFilteredDeals(deals)}
            isLoading={isLoading}
            onDelete={handleDeleteClick}
            onCreate={() => setDialogOpen(true)}
            t={t}
          />
        </TabsContent>

        <TabsContent value="my" className="mt-4">
          <DealsBoard
            deals={getFilteredDeals(myDeals)}
            isLoading={isLoading}
            onDelete={handleDeleteClick}
            onCreate={() => setDialogOpen(true)}
            t={t}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.deleteConfirmTitle') || "Are you absolutely sure?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.deleteConfirmDesc') || "This action cannot be undone. This will permanently delete the deal and remove your data from our servers."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DealsBoard({ deals, isLoading, onDelete, onCreate, t }) {
  // Group deals by status
  const dealsByStatus = {
    draft: deals?.filter(d => d.status === 'draft') || [],
    active: deals?.filter(d => d.status === 'active') || [],
    pending_documents: deals?.filter(d => d.status === 'pending_documents') || [],
    pending_signature: deals?.filter(d => d.status === 'pending_signature') || [],
    pending_payment: deals?.filter(d => d.status === 'pending_payment') || [],
    escrow_funded: deals?.filter(d => d.status === 'escrow_funded') || [],
    release_requested: deals?.filter(d => d.status === 'release_requested') || [],
    disputed: deals?.filter(d => d.status === 'disputed') || [],
    on_hold: deals?.filter(d => d.status === 'on_hold') || [],
    completed: deals?.filter(d => d.status === 'completed') || [],
    closed: deals?.filter(d => d.status === 'closed') || [],
    cancelled: deals?.filter(d => d.status === 'cancelled') || []
  };

  if (isLoading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map(i => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-20" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[1, 2].map(j => (
                <Skeleton key={j} className="h-20 w-full" />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!deals || deals.length === 0) {
    return (
      <EmptyState
        svgIcon="briefcase.svg"
        title={t('deals.empty.title')}
        description={t('deals.empty.desc')}
        action={
          <Button onClick={onCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t('deals.create')}
          </Button>
        }
      />
    );
  }

  // Helper to render a column
  const renderColumn = (statusKey, title, iconSrc, dealsList) => (
    <Card className="min-w-[280px] w-full md:w-[300px] flex-shrink-0">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <SvgIcon src={iconSrc} className="h-4 w-4 opacity-70" />
          {title} ({dealsList.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 min-h-[200px]">
        {dealsList.map(deal => (
          <DealCard key={deal._id} deal={deal} onDelete={() => onDelete(deal._id)} t={t} />
        ))}
        {dealsList.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">{t('deals.empty.title')}</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="flex overflow-x-auto pb-6 gap-6 snap-x">
      {renderColumn('draft', t('deals.column.draft') || 'Draft', 'clipboard.svg', dealsByStatus.draft)}
      {renderColumn('on_hold', t('deals.column.on_hold') || 'On Hold', 'clock.svg', dealsByStatus.on_hold)}
      {renderColumn('active', t('deals.column.active') || 'Active', 'goal.svg', dealsByStatus.active)}
      {renderColumn('pending_documents', t('deals.column.pending_documents') || 'Pending Documents', 'hourglass.svg', dealsByStatus.pending_documents)}
      {renderColumn('pending_signature', t('deals.column.pending_signature') || 'Pending Signature', 'discussion.svg', dealsByStatus.pending_signature)}
      {renderColumn('pending_payment', t('deals.column.pending_payment') || 'Pending Payment', 'coin.svg', dealsByStatus.pending_payment)}
      {renderColumn('escrow_funded', t('deals.column.escrow_funded') || 'Escrow Funded', 'protection.svg', dealsByStatus.escrow_funded)}
      {renderColumn('release_requested', t('deals.column.release_requested') || 'Release Requested', 'bell.svg', dealsByStatus.release_requested)}
      {renderColumn('disputed', t('deals.column.disputed') || 'Disputed', 'analysis.svg', dealsByStatus.disputed)}
      {renderColumn('completed', t('deals.column.completed') || 'Completed', 'trophy.svg', dealsByStatus.completed)}
      {renderColumn('closed', t('deals.column.closed') || 'Closed', 'award.svg', dealsByStatus.closed)}
      {renderColumn('cancelled', t('deals.column.cancelled') || 'Cancelled', 'shield.svg', dealsByStatus.cancelled)}
    </div>
  );
}


// Contract status progression indicator
function ContractProgress({ status }) {
  const steps = [
    { key: 'created', label: 'Created' },
    { key: 'sent', label: 'Sent' },
    { key: 'viewed', label: 'Viewed' },
    { key: 'signed', label: 'Signed' },
  ];

  const getActiveStep = (s) => {
    if (s === 'completed' || s === 'fully_signed') return 3;
    if (s === 'pending_signature') return 1;
    if (s === 'in_progress') return 1;
    return 0; // draft/new
  };

  const active = getActiveStep(status);

  return (
    <div className="flex items-center gap-1 mt-2">
      {steps.map((step, i) => (
        <React.Fragment key={step.key}>
          <div className={`h-2 w-2 rounded-full ${i <= active ? 'bg-green-500' : 'bg-muted'}`} />
          {i < steps.length - 1 && (
            <div className={`h-0.5 w-6 ${i < active ? 'bg-green-500' : 'bg-muted'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function DealCard({ deal, onDelete, t }) {
  return (
    <div
      className="p-3 rounded-lg border bg-card hover:shadow-md transition-shadow group"
      data-testid="deal-card"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <Link
          to={`/deals/${deal._id}`}
          className="font-medium text-sm hover:text-primary truncate flex-1"
          data-testid="deal-card-link"
        >
          {deal.title}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => { e.preventDefault(); onDelete(); }}
          aria-label={t('common.delete')}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground capitalize">{deal.type?.replace('_', ' ')}</span>
        <Link to={`/deals/${deal._id}`}>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
            {t('common.viewAll')}
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </div>
      <ContractProgress status={deal.status} />
    </div>
  );
}
